/// <reference path="../../../js/types.d.ts" />

import { GROK_WORKER_URL } from '../../../js/config.js';
import { HangoutState } from '../state/HangoutState.js';
import { storageManager } from '../../../core/storage/index.js';
import { storage, storageMechanism } from '../../../js/utils/storage.js';
import { AudioPlayerWithSeparation, audioPreloader } from './audio-player.js';
import { encodeMessageKey, findMessageIdByText } from './message-key-utils.js';
import { getAllCharacterIds, isGreetingMessage } from './voice-api.js';
import {
    getCurrentVoiceId,
    getCurrentVoiceLanguage,
    getVoiceSettingsSnapshot,
    buildVoiceCompositeKey
} from './voice-settings.js';

let __voiceModPromise;
let __voiceInited = false;
let __voiceRef = null;
let __displayManagerRef = null;

const DEFAULT_CHARACTER_COLOR = '#ff69b4';
const getCharacterColor = () => DEFAULT_CHARACTER_COLOR;

const hexToRgba = (hex, alpha = 1) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return `rgba(255, 105, 180, ${alpha})`;
    return `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})`;
};

const normalizeAudioEntry = (key, value) => {
    if (!value) return null;
    if (typeof value.playWithSeparation === 'function') {
        return { key, audioPlayer: value, audio: null };
    }
    if (value.audio && typeof value.audio.play === 'function') {
        return { key, audioPlayer: null, audio: value.audio };
    }
    if (typeof value.play === 'function') {
        return { key, audioPlayer: null, audio: value };
    }
    return { key, audioPlayer: null, audio: null };
};

export const audioPlayerMap = new Map();

export async function getVoice() {
    if (!__voiceModPromise) {
        __voiceModPromise = import('./hangoutVoice.js');
    }
    const { Voice } = await __voiceModPromise;
    if (!__voiceInited) {
        Voice.init({
            GROK_WORKER_URL,
            storage,
            storageMechanism,
            storageManager,
            audioPreloader,
            encodeMessageKey,
            getAllCharacterIds,
            HangoutState,
            displayManager: {
                redrawAllMessages: () => __displayManagerRef ? __displayManagerRef.redrawAllMessages() : {},
                getActiveMessages: () => __displayManagerRef ? __displayManagerRef.getActiveMessages() : []
            },
            getCharacterColor,
            hexToRgba,
            AudioPlayerWithSeparation,
            isGreetingMessage,
            findMessageIdByText
        });
        __voiceInited = true;
        __voiceRef = Voice;
    }

    try {
        const { getDisplayManager } = await import('./message-utils.js');
        __displayManagerRef = await getDisplayManager();
    } catch (error) {
        console.warn('getVoice: Failed to resolve display manager:', error);
    }

    return Voice;
}

export async function invalidateCachedVoiceForCharacter(characterId, keepVoiceId = null) {
    const Voice = await getVoice();
    Voice.invalidateCachedVoiceForCharacter?.(characterId, keepVoiceId);
}

export async function syncMessageAudioCacheAfterEdit({ characterId, previousText, nextText, bubble = null } = {}) {
    try {
        const Voice = await getVoice();
        Voice.syncCachedVoiceAfterMessageEdit?.(characterId, previousText, nextText, bubble);
    } catch (error) {
        console.warn('syncMessageAudioCacheAfterEdit failed:', error);
    }
}

export async function handleVoiceButtonClick(bubble) {
    if (bubble?.isReasoningPreview) return;
    try {
        const Voice = await getVoice();
        return Voice.handleVoiceButtonClick(bubble);
    } catch (error) {
        console.error('Voice button click failed:', error);
        if (!bubble) return;
        if (typeof bubble.setVoiceButtonState === 'function') {
            bubble.setVoiceButtonState('error');
            return;
        }
        if (bubble.voiceButtonState !== undefined) {
            bubble.voiceButtonState = 'error';
            bubble.markDirty && bubble.markDirty();
        }
    }
}

export function getCurrentAudioForCharacter(characterId, messageText = null, voiceSettings = null) {
    if (!characterId) return null;
    const normalizedCharacterId = String(characterId);

    if (messageText) {
        const settings = voiceSettings || getVoiceSettingsSnapshot(normalizedCharacterId);
        const specificKey = buildVoiceCompositeKey(normalizedCharacterId, messageText, settings);
        if (audioPlayerMap.has(specificKey)) {
            return normalizeAudioEntry(specificKey, audioPlayerMap.get(specificKey));
        }
        if (!voiceSettings) {
            const messageKey = encodeMessageKey(messageText);
            const messagePrefix = `${normalizedCharacterId}::${messageKey}::`;
            for (const [key, audioPlayer] of audioPlayerMap.entries()) {
                if (key.startsWith(messagePrefix)) {
                    return normalizeAudioEntry(key, audioPlayer);
                }
            }
        }
    }

    if (voiceSettings) return null;

    const characterPrefix = `${normalizedCharacterId}::`;
    for (const [key, audioPlayer] of audioPlayerMap.entries()) {
        if (key.startsWith(characterPrefix)) {
            return normalizeAudioEntry(key, audioPlayer);
        }
    }
    return null;
}

export function addVoicePlayListener(bubble, characterId, messageText) {
    getVoice().then(Voice => Voice.addVoicePlayListener(bubble, characterId, messageText));
}

export async function playVoice(characterId, messageText, button, messageId = null, useStreaming = true, voiceSettingsOverride = null) {
    const Voice = await getVoice();
    return Voice.playVoice(characterId, messageText, button, messageId, useStreaming, voiceSettingsOverride);
}

export { getCurrentVoiceId, getCurrentVoiceLanguage, getVoiceSettingsSnapshot, buildVoiceCompositeKey };
